import React, { useState, useEffect } from 'react';
import { Search, MapPin, Filter, PlusCircle } from 'lucide-react';
import axios from 'axios';
import { CharityPromo } from '../components/CharityPromo';
import { AuctionModal } from '../components/AuctionModal';
import { ListingRequestModal } from '../components/ListingRequestModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuctionSearch = () => {
  const [categories, setCategories] = useState([]);
  const [searchParams, setSearchParams] = useState({
    zip_code: '',
    category: 'All Categories',
    radius_miles: 50
  });
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedAuction, setSelectedAuction] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [listingModalOpen, setListingModalOpen] = useState(false);
  const [dirTotals, setDirTotals] = useState(null);

  useEffect(() => {
    fetchCategories();
    fetchDirTotals();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchDirTotals = async () => {
    try {
      const response = await axios.get(`${API}/directory/overview`);
      setDirTotals({
        auctioneers: response.data.total_auctioneers || 0,
        houses: response.data.total_houses || 0,
      });
    } catch (error) {
      console.error('Error fetching directory totals:', error);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setHasSearched(true);

    try {
      const searchData = {
        zip_code: searchParams.zip_code,
        radius_miles: parseInt(searchParams.radius_miles),
        categories: searchParams.category === 'All Categories' ? null : [searchParams.category]
      };

      const response = await axios.post(`${API}/auctions/search`, searchData);
      setResults(response.data.auctions);
    } catch (error) {
      console.error('Error searching auctions:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAuctionClick = (auction) => {
    setSelectedAuction(auction);
    setModalOpen(true);
  };

  const radiusOptions = [10, 25, 50, 100, 250, 500];

  return (
    <div className="bg-gray-50 min-h-screen" data-testid="auction-search-page">
      {/* Hero Search Section */}
      <section className="bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="search-title">
              Find Auctions Near You
            </h1>
            <p className="text-xl text-gray-200" data-testid="directory-count-tagline">
              {dirTotals
                ? `The web's premier auctioneer directory — ${dirTotals.auctioneers.toLocaleString()}+ auctioneers & ${dirTotals.houses.toLocaleString()} auction companies nationwide`
                : "The web's premier auctioneer directory — thousands of auctioneers & auction companies nationwide"}
            </p>
          </div>

          {/* Search Form */}
          <form onSubmit={handleSearch} className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-xl p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {/* ZIP Code Input */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <MapPin className="inline h-4 w-4 mr-1" />
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    data-testid="zip-input"
                    placeholder="Enter ZIP code"
                    value={searchParams.zip_code}
                    onChange={(e) => setSearchParams({...searchParams, zip_code: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                    required
                  />
                </div>

                {/* Category Select */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <Filter className="inline h-4 w-4 mr-1" />
                    Category
                  </label>
                  <select
                    value={searchParams.category}
                    data-testid="category-select"
                    onChange={(e) => setSearchParams({...searchParams, category: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                  >
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                {/* Radius Select */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Distance (Miles)
                  </label>
                  <select
                    value={searchParams.radius_miles}
                    data-testid="radius-select"
                    onChange={(e) => setSearchParams({...searchParams, radius_miles: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                  >
                    {radiusOptions.map((radius) => (
                      <option key={radius} value={radius}>{radius} miles</option>
                    ))}
                  </select>
                </div>
              </div>

              <button
                type="submit"
                data-testid="search-btn"
                disabled={loading}
                className="w-full px-8 py-4 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-lg transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Search className="h-5 w-5 mr-2" />
                {loading ? 'Searching...' : 'Search Auctions'}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Results Section */}
      {hasSearched && (
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Results Header */}
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2" data-testid="results-header">
                Looking for an auction in your area for {searchParams.category}?
              </h2>
              <p className="text-lg text-gray-600">
                {results.length > 0 ? (
                  <>
                    Here's what we found: <strong>{results.length} auction house{results.length !== 1 ? 's' : ''}</strong> currently active within <strong>{searchParams.radius_miles} miles</strong> from ZIP code <strong>{searchParams.zip_code}</strong>
                  </>
                ) : (
                  `No auction houses found within ${searchParams.radius_miles} miles from ${searchParams.zip_code} for this category. Try expanding your search radius.`
                )}
              </p>
            </div>

            {/* Results Grid with Sidebar */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Main Results */}
              <div className="lg:col-span-3">
                {results.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {results.map((auction, index) => (
                      <React.Fragment key={auction.id}>
                        <div
                          data-testid={`auction-result-${auction.id}`}
                          className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow cursor-pointer"
                          onClick={() => handleAuctionClick(auction)}
                        >
                          <div className="flex justify-between items-start mb-3">
                            <h3 className="text-xl font-bold text-gray-900">
                              {auction.name}
                            </h3>
                            {auction.distance_miles && (
                              <span className="px-3 py-1 bg-amber-100 text-amber-800 text-sm font-semibold rounded-full">
                                {auction.distance_miles} mi
                              </span>
                            )}
                          </div>
                          
                          <p className="text-gray-600 mb-4 line-clamp-2">
                            {auction.description}
                          </p>
                          
                          <div className="mb-4">
                            <p className="text-sm font-semibold text-gray-700 mb-2">Categories:</p>
                            <div className="flex flex-wrap gap-2">
                              {auction.categories.slice(0, 3).map((cat, idx) => (
                                <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                                  {cat}
                                </span>
                              ))}
                              {auction.categories.length > 3 && (
                                <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                                  +{auction.categories.length - 3} more
                                </span>
                              )}
                            </div>
                          </div>
                          
                          <div className="border-t pt-4 mt-4">
                            <p className="text-sm text-gray-600 mb-1">
                              <MapPin className="inline h-4 w-4 mr-1" />
                              {auction.location.city}, {auction.location.state} {auction.location.zip_code}
                            </p>
                            {auction.phone && (
                              <p className="text-sm text-gray-600">
                                📞 {auction.phone}
                              </p>
                            )}
                          </div>
                          
                          <button className="w-full mt-4 px-4 py-2 bg-blue-900 hover:bg-blue-800 text-white font-semibold rounded transition-colors">
                            View Auction House
                          </button>
                        </div>
                        

                      </React.Fragment>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow-lg p-12 text-center">
                    <div className="text-6xl mb-4">🔍</div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      No Results Found
                    </h3>
                    <p className="text-gray-600">
                      Try adjusting your search criteria or expanding the radius.
                    </p>
                  </div>
                )}
              </div>

              {/* Sidebar with Ads */}
              <div className="lg:col-span-1">
                <div className="sticky top-4 space-y-6">
                  {/* Search Tips */}
                  <div className="bg-white rounded-lg shadow-lg p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Search Tips</h3>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-start">
                        <span className="text-blue-500 mr-2">•</span>
                        Try increasing the search radius for more results
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-500 mr-2">•</span>
                        Select "All Categories" to see all auction houses
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-500 mr-2">•</span>
                        Click on any auction house to view their website
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-500 mr-2">•</span>
                        Contact auction houses directly for upcoming events
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>


          </div>
        </section>
      )}

      {/* Call to Action for Auction Companies - After Results */}
      {!hasSearched && (
        <>
          {/* Consumer cross-promo → CharityFundraiser.com */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <CharityPromo />
          </div>

          <section className="bg-gradient-to-r from-amber-500 to-amber-600 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col md:flex-row items-center justify-between">
                <div className="text-white mb-4 md:mb-0">
                  <h3 className="text-2xl font-bold mb-2" data-testid="cta-title">
                    Is Your Auction Company Listed?
                  </h3>
                  <p className="text-lg">
                    Join thousands of auction houses in our directory. Get discovered by bidders nationwide!
                  </p>
                </div>
                <button
                  onClick={() => setListingModalOpen(true)}
                  data-testid="request-listing-btn"
                  className="px-8 py-4 bg-white hover:bg-gray-100 text-amber-600 font-bold rounded-lg transition-colors shadow-lg flex items-center whitespace-nowrap"
                >
                  <PlusCircle className="h-6 w-6 mr-2" />
                  Request Listing
                </button>
              </div>
            </div>
          </section>

          {/* Web-Tech Services Promotion */}
          <section className="bg-gradient-to-r from-blue-900 to-blue-800 py-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="bg-white rounded-lg shadow-xl p-8 md:p-12">
                <div className="flex flex-col md:flex-row items-center gap-8">
                  <div className="flex-1">
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                      Need a Custom Auction Website Built for Your Company?
                    </h2>
                    <p className="text-lg text-gray-700 mb-6">
                      We can get you to the right place for that! Web-Tech Services specializes in AI-powered website building and mobile app development, creating professional, custom solutions tailored to your auction business needs.
                    </p>
                    <div className="space-y-3 mb-6">
                      <div className="flex items-start">
                        <svg className="h-6 w-6 text-blue-900 mr-3 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <p className="text-gray-700"><strong>AI Website Building:</strong> Fast, professional websites powered by artificial intelligence</p>
                      </div>
                      <div className="flex items-start">
                        <svg className="h-6 w-6 text-blue-900 mr-3 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <p className="text-gray-700"><strong>Mobile App Products:</strong> Native iOS and Android apps for your auction platform</p>
                      </div>
                      <div className="flex items-start">
                        <svg className="h-6 w-6 text-blue-900 mr-3 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <p className="text-gray-700"><strong>Custom Solutions:</strong> Bidding systems, payment integration, inventory management, and more</p>
                      </div>
                    </div>
                    <a
                      href="https://web-techservices.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="webtech-cta-btn"
                      className="inline-flex items-center px-8 py-4 bg-blue-900 hover:bg-blue-800 text-white font-bold rounded-lg transition-colors shadow-lg"
                    >
                      Visit Web-Tech Services.com
                      <svg className="ml-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </a>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg p-8 text-center">
                      <svg className="h-32 w-32 mx-auto text-blue-900 mb-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                      </svg>
                      <p className="text-blue-900 font-bold text-lg">Professional<br/>Auction Websites</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Auction Modal */}
      <AuctionModal 
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        auction={selectedAuction}
      />

      {/* Listing Request Modal */}
      <ListingRequestModal 
        isOpen={listingModalOpen}
        onClose={() => setListingModalOpen(false)}
      />
    </div>
  );
};
